import type { Usuario, LoginResponse, Empresa, Establecimiento, ConfiguracionEmpresa, ConfiguracionLogin, Permiso, CodigoDetraccionDto, TipoOperacionDto, SuscripcionEstado } from '../modelos/Auth';
import type { TipoCambio } from '../modelos/TipoCambio';
import { logger } from './logger';

const STORAGE_KEYS = {
  TOKEN: 'senciyo_token',
  REFRESH_TOKEN: 'senciyo_refresh_token',
  USER: 'senciyo_user',
  EXPIRES_AT: 'senciyo_expires_at',
  EMPRESA_ID: 'senciyo_empresa_id',
  ESTABLECIMIENTO_ID: 'senciyo_establecimiento_id',
  EMPRESAS: 'senciyo_empresas',
  ESTABLECIMIENTOS: 'senciyo_establecimientos',
  CONFIGURACION: 'senciyo_configuracion',
  TIPO_CAMBIO: 'senciyo_tipo_cambio',
  PERMISOS: 'senciyo_permisos',
  CODIGOS_DETRACCION: 'senciyo_codigos_detraccion',
  TIPOS_OPERACION: 'senciyo_tipos_operacion',
  SUSCRIPCIONES: 'senciyo_suscripciones',
} as const;

const LEGACY_SUSCRIPCION_KEY = 'senciyo_suscripcion';

class StorageService {
  private isBrowser = typeof window !== 'undefined';

  private safeJsonParse<T>(value: string | null, fallback: T): T {
    if (!value) return fallback;
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch {
      return fallback;
    }
  }

  private getItem(key: string): string | null {
    if (!this.isBrowser) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private setItem(key: string, value: string): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(key, value);
    } catch {
      return;
    }
  }

  private removeItem(key: string): void {
    if (!this.isBrowser) return;
    try {
      localStorage.removeItem(key);
    } catch {
      return;
    }
  }

  /**
   * Construye el objeto ConfiguracionLogin (con empresaId) a partir de una empresa.
   * Es el formato exacto que se guarda en senciyo_configuracion.
   */
  private buildConfiguracionActual(empresa: Empresa): ConfiguracionLogin | null {
    if (!empresa.configuracion) return null;
    return { empresaId: empresa.id, ...empresa.configuracion };
  }

  /**
   * Escribe senciyo_configuracion con la config de la empresa dada.
   * Si no tiene config embebida, elimina la clave.
   */
  private writeConfiguracionActual(empresa: Empresa): void {
    const config = this.buildConfiguracionActual(empresa);
    if (config) {
      this.setItem(STORAGE_KEYS.CONFIGURACION, JSON.stringify(config));
    } else {
      this.removeItem(STORAGE_KEYS.CONFIGURACION);
    }
  }

  private resolverEmpresaParaSuscripcion(empresas: Empresa[]): string | null {
    if (empresas.length === 0) return null;
    if (empresas.length === 1) return empresas[0].id;
    return (empresas.find(e => e.esPrincipal) ?? empresas[0]).id;
  }

  saveAuth(response: LoginResponse): void {
    const expiresAt = Date.now() + response.expiresIn * 1000;

    this.clearWorkspace();

    this.setItem(STORAGE_KEYS.TOKEN, response.token);
    this.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refreshToken);
    this.setItem(STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());

    const user: Usuario = {
      id: response.id,
      nombre: response.nombre,
      apellido: response.apellido,
      email: response.email,
      esActivo: true,
      requiereCambioPassword: response.requiereCambioPassword,
      esOnboarding: response.esOnboarding,
      esSuperAdminSenciyo: response.esSuperAdminSenciyo ?? false,
      esCreadoPorApiKey: response.esCreadoPorApiKey,
      esCuentaDemo: response.esCuentaDemo ?? false,
    };

    this.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

    if (response.empresas && response.empresas.length > 0) {
      // Fusionar la configuración de cada empresa dentro del objeto empresa
      const configPorEmpresa = new Map(
        (response.configuraciones ?? []).map(c => [c.empresaId, c])
      );

      const empresasConConfig: Empresa[] = response.empresas.map(empresa => {
        const cfg = configPorEmpresa.get(empresa.id);
        if (!cfg) return empresa;
        const { empresaId: _empresaId, ...rest } = cfg;
        return { ...empresa, configuracion: rest };
      });

      this.setItem(STORAGE_KEYS.EMPRESAS, JSON.stringify(empresasConConfig));
    } else {
      this.removeItem(STORAGE_KEYS.EMPRESAS);
    }

    if (response.establecimientos && response.establecimientos.length > 0) {
      this.setItem(STORAGE_KEYS.ESTABLECIMIENTOS, JSON.stringify(response.establecimientos));
    } else {
      this.removeItem(STORAGE_KEYS.ESTABLECIMIENTOS);
    }

    if (response.permisos && response.permisos.length > 0) {
      this.setItem(STORAGE_KEYS.PERMISOS, JSON.stringify(response.permisos));
    } else {
      this.removeItem(STORAGE_KEYS.PERMISOS);
    }

    if (response.codigosDetraccion && response.codigosDetraccion.length > 0) {
      this.setItem(STORAGE_KEYS.CODIGOS_DETRACCION, JSON.stringify(response.codigosDetraccion));
    } else {
      this.removeItem(STORAGE_KEYS.CODIGOS_DETRACCION);
    }

    if (response.tiposOperacion && response.tiposOperacion.length > 0) {
      this.setItem(STORAGE_KEYS.TIPOS_OPERACION, JSON.stringify(response.tiposOperacion));
    } else {
      this.removeItem(STORAGE_KEYS.TIPOS_OPERACION);
    }

    this.removeItem(STORAGE_KEYS.SUSCRIPCIONES);
    this.removeItem(LEGACY_SUSCRIPCION_KEY);
    if (response.suscripcion && response.empresas) {
      const empresaResuelta = this.resolverEmpresaParaSuscripcion(response.empresas);
      if (empresaResuelta) {
        this.setItem(STORAGE_KEYS.SUSCRIPCIONES, JSON.stringify({ [empresaResuelta]: response.suscripcion }));
      }
    }

    // senciyo_configuracion se escribe en setWorkspace (llamado por autoSelectWorkspace)
    // Si no hay auto-selección, limpiar para evitar config de sesión anterior
    this.removeItem(STORAGE_KEYS.CONFIGURACION);
    this.dispatchWorkspaceUpdated();
  }

  /**
   * Selecciona el workspace activo y actualiza automáticamente senciyo_configuracion
   * con la config de la empresa seleccionada.
   */
  setWorkspace(empresaId: string, establecimientoId?: string): void {
    this.setItem(STORAGE_KEYS.EMPRESA_ID, empresaId);
    if (establecimientoId) {
      this.setItem(STORAGE_KEYS.ESTABLECIMIENTO_ID, establecimientoId);
    } else {
      this.removeItem(STORAGE_KEYS.ESTABLECIMIENTO_ID);
    }

    // Actualizar senciyo_configuracion con la empresa recién seleccionada
    const empresa = this.getEmpresas().find(e => e.id === empresaId);
    if (empresa) {
      this.writeConfiguracionActual(empresa);
    } else {
      this.removeItem(STORAGE_KEYS.CONFIGURACION);
    }
    this.dispatchWorkspaceUpdated();
  }

  getToken(): string | null {
    return this.getItem(STORAGE_KEYS.TOKEN);
  }

  getRefreshToken(): string | null {
    return this.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  getUser(): Usuario | null {
    const userStr = this.getItem(STORAGE_KEYS.USER);
    return this.safeJsonParse<Usuario | null>(userStr, null);
  }

  updateUser(campos: Partial<Usuario>): void {
    const actual = this.getUser();
    if (!actual) return;
    this.setItem(STORAGE_KEYS.USER, JSON.stringify({ ...actual, ...campos }));
  }

  getEmpresas(): Empresa[] {
    const empresasStr = this.getItem(STORAGE_KEYS.EMPRESAS);
    return this.safeJsonParse<Empresa[]>(empresasStr, []);
  }

  private dispatchWorkspaceUpdated(): void {
    if (!this.isBrowser) return;
    window.dispatchEvent(new CustomEvent('senciyo:workspace-updated'));
  }

  updateEmpresas(empresas: Empresa[]): void {
    try {
      this.setItem(STORAGE_KEYS.EMPRESAS, JSON.stringify(empresas));
      this.dispatchWorkspaceUpdated();
    } catch (error) {
      logger.error('Error al actualizar empresas en localStorage:', error);
    }
  }

  getEstablecimientos(): Establecimiento[] {
    const establecimientosStr = this.getItem(STORAGE_KEYS.ESTABLECIMIENTOS);
    return this.safeJsonParse<Establecimiento[]>(establecimientosStr, []);
  }

  updateEstablecimientos(establecimientos: Establecimiento[]): void {
    try {
      this.setItem(STORAGE_KEYS.ESTABLECIMIENTOS, JSON.stringify(establecimientos));
      this.dispatchWorkspaceUpdated();
    } catch (error) {
      logger.error('Error al actualizar establecimientos en localStorage:', error);
    }
  }

  getConfiguracionActual(): ConfiguracionLogin | null {
    const raw = this.getItem(STORAGE_KEYS.CONFIGURACION);
    return this.safeJsonParse<ConfiguracionLogin | null>(raw, null);
  }

  getConfiguracionByEmpresa(empresaId: string): ConfiguracionEmpresa | null {
    return this.getEmpresas().find(e => e.id === empresaId)?.configuracion ?? null;
  }

  /**
   * Parchea la config embebida en la empresa dentro de senciyo_empresas.
   * Si la empresa modificada es la actualmente seleccionada,
   * también actualiza senciyo_configuracion automáticamente.
   */
  updateConfiguracionEmpresa(empresaId: string, campos: Partial<ConfiguracionEmpresa>): void {
    const empresas = this.getEmpresas();
    let empresaActualizada: Empresa | null = null;

    const updated = empresas.map(e => {
      if (e.id !== empresaId) return e;
      const nueva = { ...e, configuracion: { ...e.configuracion, ...campos } as ConfiguracionEmpresa };
      empresaActualizada = nueva;
      return nueva;
    });

    if (empresaActualizada && this.getEmpresaId() === empresaId) {
      this.writeConfiguracionActual(empresaActualizada);
    }

    this.updateEmpresas(updated);
  }

  syncEntity(
    operation: 'CREAR' | 'ACTUALIZAR' | 'ELIMINAR',
    entityType: 'empresas' | 'establecimientos',
    data: Empresa | Establecimiento | { id: string }
  ): void {
    try {
      const currentList = entityType === 'empresas' ? this.getEmpresas() : this.getEstablecimientos();
      let updatedList: (Empresa | Establecimiento)[] = [];

      switch (operation) {
        case 'CREAR':
          updatedList = [data as Empresa | Establecimiento, ...currentList];
          break;

        case 'ACTUALIZAR':
          updatedList = currentList.map(item =>
            item.id === (data as { id: string }).id
              ? { ...item, ...(data as Empresa | Establecimiento) }
              : item
          );
          break;

        case 'ELIMINAR':
          updatedList = currentList.filter(item => item.id !== (data as { id: string }).id);
          break;
      }

      if (entityType === 'empresas') {
        this.updateEmpresas(updatedList as Empresa[]);
      } else {
        this.updateEstablecimientos(updatedList as Establecimiento[]);
      }
    } catch (error) {
      logger.error(`Error al sincronizar ${entityType} en localStorage (${operation}):`, error);
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const expiresAtStr = this.getItem(STORAGE_KEYS.EXPIRES_AT);

    if (!token || !expiresAtStr) return false;

    const expiresAt = parseInt(expiresAtStr, 10);
    return Date.now() < expiresAt;
  }

  isTokenExpired(): boolean {
    const expiresAtStr = this.getItem(STORAGE_KEYS.EXPIRES_AT);
    if (!expiresAtStr) return true;

    const expiresAt = parseInt(expiresAtStr, 10);
    return Date.now() >= expiresAt;
  }

  saveWorkspace(empresaId: string, establecimientoId?: string): void {
    this.setWorkspace(empresaId, establecimientoId);
  }

  getEmpresaId(): string | null {
    return this.getItem(STORAGE_KEYS.EMPRESA_ID);
  }

  getEmpresaActual(): Empresa | null {
    const empresaId = this.getEmpresaId();
    if (!empresaId) return null;
    return this.getEmpresas().find(e => e.id === empresaId) ?? null;
  }

  getEstablecimientoId(): string | null {
    return this.getItem(STORAGE_KEYS.ESTABLECIMIENTO_ID);
  }

  hasWorkspaceContext(): boolean {
    return !!this.getEmpresaId();
  }

  saveTipoCambio(tipoCambio: TipoCambio): void {
    this.setItem(STORAGE_KEYS.TIPO_CAMBIO, JSON.stringify(tipoCambio));
  }

  getTipoCambio(): TipoCambio | null {
    const raw = this.getItem(STORAGE_KEYS.TIPO_CAMBIO);
    return this.safeJsonParse<TipoCambio | null>(raw, null);
  }

  clearTipoCambio(): void {
    this.removeItem(STORAGE_KEYS.TIPO_CAMBIO);
  }

  getPermisos(): Permiso[] {
    const raw = this.getItem(STORAGE_KEYS.PERMISOS);
    return this.safeJsonParse<Permiso[]>(raw, []);
  }

  getCodigosDetraccion(): CodigoDetraccionDto[] {
    const raw = this.getItem(STORAGE_KEYS.CODIGOS_DETRACCION);
    return this.safeJsonParse<CodigoDetraccionDto[]>(raw, []);
  }

  saveCodigosDetraccion(codigos: CodigoDetraccionDto[]): void {
    this.setItem(STORAGE_KEYS.CODIGOS_DETRACCION, JSON.stringify(codigos));
  }

  getTiposOperacion(): TipoOperacionDto[] {
    const raw = this.getItem(STORAGE_KEYS.TIPOS_OPERACION);
    return this.safeJsonParse<TipoOperacionDto[]>(raw, []);
  }

  saveTiposOperacion(tipos: TipoOperacionDto[]): void {
    this.setItem(STORAGE_KEYS.TIPOS_OPERACION, JSON.stringify(tipos));
  }

  getSuscripciones(): Record<string, SuscripcionEstado> {
    const raw = this.getItem(STORAGE_KEYS.SUSCRIPCIONES);
    return this.safeJsonParse<Record<string, SuscripcionEstado>>(raw, {});
  }

  saveSuscripciones(mapa: Record<string, SuscripcionEstado>): void {
    const actual = this.getSuscripciones();
    this.setItem(STORAGE_KEYS.SUSCRIPCIONES, JSON.stringify({ ...actual, ...mapa }));
    this.dispatchWorkspaceUpdated();
  }

  getSuscripcion(): SuscripcionEstado | null {
    const empresaId = this.getEmpresaId();
    if (!empresaId) return null;
    return this.getSuscripciones()[empresaId] ?? null;
  }

  saveSuscripcion(empresaId: string, suscripcion: SuscripcionEstado): void {
    this.saveSuscripciones({ [empresaId]: suscripcion });
  }

  clearSuscripcion(): void {
    this.removeItem(STORAGE_KEYS.SUSCRIPCIONES);
  }

  clearAuth(): void {
    this.removeItem(STORAGE_KEYS.TOKEN);
    this.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    this.removeItem(STORAGE_KEYS.USER);
    this.removeItem(STORAGE_KEYS.EXPIRES_AT);
    this.removeItem(STORAGE_KEYS.EMPRESAS);
    this.removeItem(STORAGE_KEYS.ESTABLECIMIENTOS);
    this.removeItem(STORAGE_KEYS.CONFIGURACION);
    this.removeItem(STORAGE_KEYS.PERMISOS);
    this.removeItem(STORAGE_KEYS.CODIGOS_DETRACCION);
    this.removeItem(STORAGE_KEYS.TIPOS_OPERACION);
    this.removeItem(STORAGE_KEYS.SUSCRIPCIONES);
  }

  clearWorkspace(): void {
    this.removeItem(STORAGE_KEYS.EMPRESA_ID);
    this.removeItem(STORAGE_KEYS.ESTABLECIMIENTO_ID);
  }

  clearAll(): void {
    this.clearAuth();
    this.clearWorkspace();
    this.clearTipoCambio();
    this.dispatchWorkspaceUpdated();
  }

  updateToken(token: string, refreshToken: string, expiresIn: number): void {
    const expiresAt = Date.now() + expiresIn * 1000;
    this.setItem(STORAGE_KEYS.TOKEN, token);
    this.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    this.setItem(STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());
  }
}

export const storageService = new StorageService();
